import { ChangeDetectionStrategy, Component, HostListener, ChangeDetectorRef, ElementRef, ViewChild } from '@angular/core';
import { PopoverController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { AuthenticationService } from '@omni/services/authentication.service';
import { DEVICE_EVENT, DeviceService } from '@omni/services/device/device.service';
import { DiskService } from '@omni/services/disk/disk.service';
import { EventsService } from '@omni/services/events/events.service';
import { FeatureActionsService } from '@omni/services/feature-actions/feature-actions.service';
import { LocationOfflineService } from '@omni/services/location/location.service';
import { ResourceService } from '@omni/services/resource/resource.service';
import { Subject, BehaviorSubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UserStatusPopoverComponent } from '../user-status-popover/user-status-popover.component';
import { RepStatusOptionModel, REP_STATUS } from '@omni/models/rep-status-model';
import { RepServices } from '@omni/data-services/rep/rep.services';
import { FeatureActionsMap } from '@omni/classes/authentication/user.class';
import { InboundMeetingService } from '@omni/services/meeting/inbound-meeting.service';
import { TrackingEventNames, TrackService } from '@omni/services/logging/tracking.service';
import { OfflineSyncUtility } from '@omni/utility/offline-sync.utility';
import { HTTPRequestTraceUtility } from '@omni/utility/util';
import { NotificationService } from '@omni/services/notification/notification.service';
import { DateTimeFormatsService } from '@omni/services/date-time-formats/date-time-formats.service';
import { SetBookingDataService } from '@omni/data-services/set-booking/set-booking.data.service';
import { AlertService } from '@omni/services/alert/alert.service';
import { LoginService } from '@omni/services/msal/login.service';
import { NavigationService, PageName } from '@omni/services/navigation/navigation.service';
import { UIService } from '@omni/services/ui/ui.service';
import { ActivityService } from '@omni/services/activity/activity.service';
import { UserProfilePhotoService } from '@omni/services/user-profile-photo/user-profile-photo.service';
export interface RepStatusOptionWithColor extends RepStatusOptionModel {
  color: string;
}

@Component({
  selector: 'profile-popup',
  templateUrl: './profile-popup.component.html',
  styleUrls: ['./profile-popup.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProfilePopupComponent {

  private readonly destroy$: Subject<boolean> = new Subject<boolean>();

  public userName: string;
  public userPosition: string;
  public userEmail: string;
  public dbEncryptionEnabled: string;
  public userStatus: RepStatusOptionWithColor | undefined;
  public syncDateString: string;
  public syncTimeString: string;
  public backgroundUploadInProgress: boolean = false;

  userStatuses: RepStatusOptionWithColor[] = [];
  private statusPopover: HTMLIonPopoverElement;
  private _inboundRequestCount: number = 0;
  public disableStatus: boolean = false;
  private hasInboundEnabled: boolean = false;
  
  // Observable for syncing user statuses to the popover
  public userStatusesSubject = new BehaviorSubject<RepStatusOptionWithColor[]>([]);

  @ViewChild('statusIcon', { static: false, read: ElementRef }) statusIconRef: ElementRef<HTMLElement>;

  constructor(
    public readonly popoverCtrl: PopoverController,
    public readonly device: DeviceService,
    public readonly authService: AuthenticationService,
    public readonly events: EventsService,
    public readonly faService: FeatureActionsService,
    public readonly locationService: LocationOfflineService,
    public readonly resourceService: ResourceService,
    public readonly diskService: DiskService,
    public readonly translate: TranslateService,
    private readonly repService: RepServices,
    private readonly inboundMeetingService: InboundMeetingService,
    private readonly trackingService: TrackService,
    private readonly offlineSync: OfflineSyncUtility,
    private readonly notificationService: NotificationService,
    private readonly cdr: ChangeDetectorRef,
    private readonly dateTimeFormatsService: DateTimeFormatsService,
    private readonly setBookingDataService: SetBookingDataService,
    private readonly alertService: AlertService,
    private readonly loginService: LoginService,
    private readonly navService: NavigationService,
    private readonly uiService: UIService,
    private readonly activityService: ActivityService,
    public readonly userProfilePhoto: UserProfilePhotoService,
  ) {
    this.initFeatureActions();
    this.buildUserStatuses();
  }

  private buildUserStatuses() {
    this.userStatuses = [
      { userState: REP_STATUS.ONLINE.userState, statusDisplayTxt: this.translate.instant('ONLINE'), selected: false, disabled: false, color: '#8BC34A' },
      { userState: REP_STATUS.AVAILABLE.userState, statusDisplayTxt: this.translate.instant('AVAILABLE'), selected: false, disabled: true, color: '#2196F3' },
      { userState: REP_STATUS.STANDBY.userState, statusDisplayTxt: this.translate.instant('STANDBY'), selected: false, disabled: true, color: '#FFC107' }
    ];
    if (this.device.isNativeApp && !this.userStatuses.find(s => s.userState === REP_STATUS.OFFLINE.userState)) {
      this.userStatuses.push({
        userState: REP_STATUS.OFFLINE.userState,
        statusDisplayTxt: this.translate.instant('OFFLINE'),
        selected: false,
        disabled: false,
        color: '#F44336'
      });
    }
    // Sync the initial user statuses to the subject
    this.userStatusesSubject.next(this.userStatuses);
  }

  private initFeatureActions() {
    this.hasInboundEnabled = this.authService.hasFeatureAction(FeatureActionsMap.INBOUND_MEETING);
  }

  private _initStatusSelectOptions() {
    this.disableStatus = false;
    this.userStatuses.forEach((status) => {
      if (status.userState === REP_STATUS.ONLINE.userState || status.userState === REP_STATUS.OFFLINE.userState) {
        status.disabled = this._inboundRequestCount > 0;
      }
      if (status.userState === REP_STATUS.STANDBY.userState || status.userState === REP_STATUS.AVAILABLE.userState) {
        status.disabled = !this.hasInboundEnabled || this._inboundRequestCount > 0;
      }
    });
    // Sync updated user statuses to the subject
    this.userStatusesSubject.next(this.userStatuses);
  }

  public getInitials(): string {
    if (!this.getUserName()) return '';
    const name = this.getUserName();
    const nameParts = name.split(' ');
    
    if (nameParts.length === 1) {
      return name.slice(0, 2).toUpperCase();
    }
    return nameParts
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }

  public getUserName(): string {
    return this.userName ? this.userName : this.authService.user.displayName;
  }

  public getUserUserLocation(): string {
    return this.device.isOffline ? this.translate.instant('LOCATION_SERVICE_UNAVAILABLE') : this.authService.user.currentLocation?.name;
  }

  private readonly _deviceConnectionChangeHandler: () => void = () => {
    this._setStatus(true);
  }

  public ngOnInit() {
    this.events.observe('sync:completed').pipe(
      takeUntil(this.destroy$))
      .subscribe(err => {
        if (!err) {
          this.setUserInfo();
          this.displayLastSync();
        }
      });
    this.device.appStateAndNetworkConnectionChange.pipe(takeUntil(this.destroy$)).subscribe((change: { isBg: boolean, isOffline: boolean }) => {
      if (this.device.isNativeApp) {
        this._deviceConnectionChangeHandler();
      }
    });
    this.inboundMeetingService.inboundMeetingRequestArrayObservable.pipe(takeUntil(this.destroy$)).subscribe(
      (requestArray: any[]) => {
        this._inboundRequestCount = requestArray.length;
        this._initStatusSelectOptions();
      }
    );
    this.device.isBackgroundUploadInProgressObservable.pipe(takeUntil(this.destroy$)).subscribe(inProgress => {
      this.backgroundUploadInProgress = inProgress;
    });
    this._setStatus();
    this.displayLastSync();
  }

  private loadUserInfoAndSet() {
    this.authService.getOfflineUser().then((user) => {
      if (user) {
        this.userName = user.displayName;
        this.userPosition = user.positionName;
        this.userEmail = user.mail;
        this.dbEncryptionEnabled = this.diskService.isDBEncryptionEnabled ? '*' : '';
      } else {
        console.warn('menu-toggle: local user info not available..');
      }
    }).catch(err => console.error('menu-toggle: loadUserInfoAndSet: ', err));
  }

  private setUserInfo() {
    if (this.device.isOffline && !this.authService.user) {
      this.loadUserInfoAndSet();
    } else if (this.authService.user) {
      this.userName = this.authService.user.displayName;
      this.userPosition = this.authService.user.positionName;
    } else {
      console.warn('menu-toggle: in-memory user info not available..');
      this.loadUserInfoAndSet();
    }
  }

  close() {
    this.popoverCtrl.dismiss();
  }

  async openStatusPopover(ev: Event) {
    const el = this.statusIconRef?.nativeElement;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const fakeEvent = {
      target: {
        getBoundingClientRect: () => rect
      }
    };
    this.statusPopover = await this.popoverCtrl.create({
      component: UserStatusPopoverComponent,
      event: fakeEvent as any,
      translucent: true,
      cssClass: 'user-status-popover',
      componentProps: {
        selectedStatus: this.userStatus?.userState,
        userStatuses$: this.userStatusesSubject.asObservable()
      }
    });
    await this.statusPopover.present();
    const { data } = await this.statusPopover.onDidDismiss();
    if (data) {
      await this.changeStatus(data);
    }
  }

  async changeStatus(userState: string) {
    if (!this.device.isNativeApp && userState === REP_STATUS.OFFLINE.userState) {
      userState = REP_STATUS.ONLINE.userState;
    }
    const oldCurrentState = this.repService.getCurrentUserState();
    if (userState === oldCurrentState) return;
    this.trackingService.tracking('LoginStatusChange', TrackingEventNames.LOGIN);
    this.disableStatus = true;
    try {
      await this.repService.setCurrentState({ userState });
      const newCurState = this.repService.getCurrentUserState();
      const newPrevState = this.repService.getPreviousUserState();
      this.repService.saveLastUserState(newCurState);
      this.userStatuses.forEach(s => s.selected = (s.userState === newCurState));
      this.userStatus = this.userStatuses.find(s => s.userState === newCurState);
      this.cdr.markForCheck();
      if (newPrevState === REP_STATUS.OFFLINE.userState && newPrevState !== newCurState) {
        this.device.isOffline = this.device.isUserStateOffline = false;
        this.offlineSync.clear();
        this.events.publish('clearInterval');
        this.authService.shouldTrySyncingDuringAppLaunch = true;
        if (!this.device.isNativeApp && this.navService.getCurrentMasterPageName() !== PageName.GoAppActivitiesPageComponent) {
          this.navService.setRootWithTracking(PageName.GoAppActivitiesPageComponent, PageName.GoAppActivitiesPageComponent, { __source: 'dashboard' }, PageName.GoAppActivitiesPageComponent);
        }
        this.events.publish('home:runUpdateSync', { syncType: DEVICE_EVENT.userStatusBackToOnline });
        this.popoverCtrl.dismiss();
      } else if (newCurState === REP_STATUS.OFFLINE.userState && newPrevState !== newCurState) {
        this.device.isOffline = this.device.isUserStateOffline = true;
        this.offlineSync.setOffline();
        // Trigger immediate offline handling across components (e.g. stop metric loaders).
        this.events.publish('device:deviceIsOffline');
        this.events.publish('offlineNotification');
        if (HTTPRequestTraceUtility.getInstance().isAtleastOneNetworkCallInProgress()) {
          this.notificationService.notify(
            this.translate.instant('BACKGROUND_SYNC_IN_PROGRESS_INTURRUPT'),
            'Home Page'
          );
        }
      }
      if (newCurState === REP_STATUS.ONLINE.userState && this.device.isOffline) {
        this.device.isOffline = this.device.isUserStateOffline = false;
      }
      this._initStatusSelectOptions();
      this.events.publish('userStateChanged');
      // Sync updated user statuses to the subject after status change
      this.userStatusesSubject.next(this.userStatuses);
    } catch (error) {
      console.error('changeStatus: ', error);
      if (userState !== REP_STATUS.OFFLINE.userState) {
        this.userStatus = this.userStatuses.find(s => s.userState === this.repService.getCurrentUserState());
        this.device.isOffline = this.device.isUserStateOffline = this.userStatus?.userState === REP_STATUS.OFFLINE.userState;
      } else {
        this.repService.setCurrentUserState(REP_STATUS.OFFLINE.userState);
        this.repService.setPreviousUserState(oldCurrentState);
        this.device.isOffline = this.device.isUserStateOffline = this.repService.isOfflineState = true;
        this.repService.saveLastUserState(REP_STATUS.OFFLINE.userState);
      }
      this._initStatusSelectOptions();
      this.cdr.markForCheck();
      // Sync updated user statuses to the subject after error recovery
      this.userStatusesSubject.next(this.userStatuses);
    }
  }

  @HostListener('window:resize')
  onResize() {
    if (this.statusPopover) {
      this.statusPopover.dismiss();
    }
    this.close();
  }

  public async refreshData() {
    this.popoverCtrl.dismiss();
    let flag = await this.setBookingDataService.checkSetBookingDiscardConfirmationRequired().catch(err=>{});
    if(!flag) return;
    if (!this.device.isNativeApp && this.navService.getCurrentMasterPageName() !== PageName.GoAppActivitiesPageComponent) {
      await this.navService.setRootWithTracking(PageName.GoAppActivitiesPageComponent, PageName.GoAppActivitiesPageComponent, { __source: 'dashboard' }, PageName.GoAppActivitiesPageComponent);
      this.uiService.toolsActivityActive = true;
      this.events.publish('footer:selectedViewChanged', 'dashboard');
    }
    this.trackingService.tracking('SyncClicked', TrackingEventNames.SYNC);
    this.events.publish('home:runUpdateSync');
  }

  displayLastSync() {
    const lastSync = (localStorage.getItem('lastFullSyncTime')) ? new Date(parseInt(localStorage.getItem('lastFullSyncTime'))) : 'Not Synced';
    if (this.dateTimeFormatsService.isValidDate(new Date(lastSync))) {
      if (this.dateTimeFormatsService.isTodayDate(lastSync)) {
        this.syncDateString = this.translate.instant('TODAY');
      } else if (this.dateTimeFormatsService.isYesterdayDate(lastSync)) {
        this.syncDateString = this.translate.instant('YESTERDAY');
      } else {
        this.syncDateString = this.dateTimeFormatsService.formatDate(lastSync, this.dateTimeFormatsService.dateToUpper);
      }
      this.syncTimeString = this.dateTimeFormatsService.formatDate(lastSync, this.dateTimeFormatsService.timeToUpper);
    } else {
      this.syncDateString = this.syncTimeString = '';
    }
  }

  public async logout() {
    let flag = await this.setBookingDataService.checkSetBookingDiscardConfirmationRequired().catch(err=>{});
    if(!flag) return;
    this.alertService.showAlert({
      title: this.translate.instant('LOGOUT_QUES_MARK'),
      message: this.translate.instant('LOGOUT_CONFIRMATION')
    }, this.translate.instant('LOGOUT')
    ).then(res => {
      if (res.role == "ok") {
        this.trackingService.tracking('Logout', TrackingEventNames.LOGOUT);
        this.loginService.logout();
      }
    });
  }

  public openSettings() {
    this.activityService.activityOpenedFromTodo = false;
    this.events.publish('clearSelectedView');
    if (this.device.isMobileDevicePortrait) {
      this.uiService.showRightPane = false;
      this.activityService.selectedActivity = null;
    }
    if (this.navService.getCurrentMasterPageName() !== PageName.GoAppActivitiesPageComponent) {
      this.navService.popToRootWithPageTracking();
    }
    this.popoverCtrl.dismiss();
    this.navService.pushWithPageTracking(PageName.GoAppSettingsComponent, PageName.GoAppSettingsComponent, null, PageName.GoAppSettingsComponent);
  }

  private async _setStatus(connectionChanged = false) {
    if (connectionChanged) {
      if (this.device.isOffline) {
        if (this.device.isDeviceRealOffline) {
          this.userStatuses.forEach((status) => status.disabled = true);
        } else {
          this._initStatusSelectOptions();
        }
      } else {
        this._initStatusSelectOptions();
      }
    }
    this.setUserInfo();
    let currentState: string;
    if (this.device.isOffline) {
      // When the device has truly lost network connectivity (not a user-initiated
      // status change), reflect OFFLINE in the popup regardless of the server-known
      // rep status. We intentionally do NOT mutate repService.userState here so that
      // when the network returns, the original rep status is restored via the else
      // branch below (which re-fetches getCurrentState()).
      if (this.device.isNativeApp && this.device.isDeviceRealOffline) {
        currentState = REP_STATUS.OFFLINE.userState;
      } else {
        currentState = this.repService.getCurrentUserState() || REP_STATUS.OFFLINE.userState;
      }
    } else {
      currentState = this.repService.getCurrentUserState();
      if (!currentState || (connectionChanged && !this.device.isOffline && currentState !== REP_STATUS.OFFLINE.userState)) {
        await this.repService.getCurrentState();
        currentState = this.repService.getCurrentUserState();
      }
    }
    if (!this.device.isNativeApp && currentState === REP_STATUS.OFFLINE.userState) {
      currentState = REP_STATUS.ONLINE.userState;
      this.repService.setCurrentUserState(REP_STATUS.ONLINE.userState);
    }
    this.userStatuses.forEach(s => s.selected = (s.userState === currentState));
    this.userStatus = this.userStatuses.find(s => s.userState === currentState);
    // Sync updated user statuses to the subject after status update
    this.userStatusesSubject.next(this.userStatuses);
    this.cdr.markForCheck();
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
} 